/**
 * Frictionless Temporary Storage API
 * Handles unauthenticated menu storage for frictionless onboarding
 */

import { getApiUrl } from '@/config/api';

export interface SessionResponse {
  session_id: string;
  expires_at: string;
  message: string;
}

export interface MenuValidationError {
  isValidationError: true;
  error: string;
  message: string;
  suggestion: string;
}

export interface TempUploadResponse {
  success: boolean;
  session_id: string;
  file_info: {
    filename: string;
    original_filename: string;
    file_type: string;
    size: number;
    uploaded_at: string;
  };
  message: string;
}

export interface MenuSelectionData {
  restaurant_data: any;
  selected_menus: any[];
}

class FrictionlessTempApi {
  /**
   * Create a new temporary session
   */
  async createSession(restaurantName?: string, location?: string, source: string = 'manual'): Promise<SessionResponse> {
    const response = await fetch(getApiUrl('frictionless/create-session'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        restaurant_name: restaurantName,
        location,
        source
      })
    });

    if (!response.ok) {
      // Try to parse as JSON first
      let errorMessage = 'Failed to create session';
      try {
        const error = await response.json();
        errorMessage = error.detail || error.message || errorMessage;
      } catch (e) {
        // If response is not JSON (likely HTML error page), use status text
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Upload a menu file to temporary storage
   */
  async uploadTempFile(sessionId: string, file: File, fileType: string = 'menu'): Promise<TempUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);

    const response = await fetch(getApiUrl(`frictionless/upload-temp/${sessionId}`), {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      // Try to parse as JSON first
      let errorMessage = 'Failed to upload file';
      try {
        const error = await response.json();

        // Check if this is a menu validation error
        if (typeof error.detail === 'object' && error.detail.error === 'invalid_menu_pdf') {
          // Create a custom error with validation details
          const validationError: MenuValidationError = {
            isValidationError: true,
            error: error.detail.error,
            message: error.detail.message,
            suggestion: error.detail.suggestion
          };
          // Throw error with both message and suggestion
          const fullMessage = `${validationError.message}\n\n${validationError.suggestion}`;
          const err = new Error(fullMessage);
          (err as any).validationError = validationError;
          throw err;
        }

        // Handle regular errors
        errorMessage = error.detail || error.message || errorMessage;
      } catch (e) {
        // If it's already our custom error, re-throw it
        if ((e as any).validationError) {
          throw e;
        }
        // If response is not JSON (likely HTML error page), use status text
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Alias for uploadTempFile for simpler API
   */
  async uploadFile(sessionId: string, file: File): Promise<TempUploadResponse> {
    return this.uploadTempFile(sessionId, file);
  }

  /**
   * Upload multiple menu files to temporary storage (for multiple images)
   */
  async uploadMultipleFiles(sessionId: string, files: File[], fileType: string = 'menu'): Promise<TempUploadResponse> {
    const formData = new FormData();
    
    // Append all files to the form data
    files.forEach((file, index) => {
      formData.append('files', file);  // Note: 'files' not 'file' for multiple
    });
    formData.append('file_type', fileType);

    const response = await fetch(getApiUrl(`frictionless/upload-temp-multiple/${sessionId}`), {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      // Try to parse as JSON first
      let errorMessage = 'Failed to upload files';
      try {
        const error = await response.json();

        // Check if this is a menu validation error
        if (typeof error.detail === 'object' && error.detail.error === 'invalid_menu_pdf') {
          // Create a custom error with validation details
          const validationError: MenuValidationError = {
            isValidationError: true,
            error: error.detail.error,
            message: error.detail.message,
            suggestion: error.detail.suggestion
          };
          // Throw error with both message and suggestion
          const fullMessage = `${validationError.message}\n\n${validationError.suggestion}`;
          const err = new Error(fullMessage);
          (err as any).validationError = validationError;
          throw err;
        }

        // Handle regular errors
        errorMessage = error.detail || error.message || errorMessage;
      } catch (e) {
        // If it's already our custom error, re-throw it
        if ((e as any).validationError) {
          throw e;
        }
        // If response is not JSON (likely HTML error page), use status text
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();
    
    // Transform the response to match single file format for compatibility
    // The backend returns file_infos array, but we need to maintain compatibility
    return {
      success: result.success,
      session_id: result.session_id,
      file_info: {
        filename: result.file_infos?.map((f: any) => f.filename).join(', ') || 'multiple_files',
        file_type: fileType,
        file_count: result.file_count || files.length
      },
      message: result.message
    };
  }

  /**
   * Save menu selection from search results
   */
  async saveMenuSelection(sessionId: string, data: MenuSelectionData): Promise<any> {
    const response = await fetch(getApiUrl('frictionless/save-menu-selection'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        session_id: sessionId,
        ...data
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to save menu selection');
    }

    return response.json();
  }

  /**
   * Get session data
   */
  async getSessionData(sessionId: string): Promise<any> {
    const response = await fetch(getApiUrl(`frictionless/session/${sessionId}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Session not found or expired');
    }

    return response.json();
  }

  /**
   * Delete a session
   */
  async deleteSession(sessionId: string): Promise<void> {
    const response = await fetch(getApiUrl(`frictionless/session/${sessionId}`), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete session');
    }
  }

  /**
   * Store session ID in localStorage for persistence
   */
  storeSessionId(sessionId: string): void {
    localStorage.setItem('frictionless_session_id', sessionId);
    localStorage.setItem('frictionless_session_timestamp', new Date().toISOString());
  }

  /**
   * Retrieve stored session ID
   */
  getStoredSessionId(): string | null {
    const sessionId = localStorage.getItem('frictionless_session_id');
    const timestamp = localStorage.getItem('frictionless_session_timestamp');
    
    if (!sessionId || !timestamp) return null;
    
    // Check if session is older than 2 hours
    const sessionTime = new Date(timestamp);
    const now = new Date();
    const hoursDiff = (now.getTime() - sessionTime.getTime()) / (1000 * 60 * 60);
    
    if (hoursDiff > 2) {
      this.clearStoredSession();
      return null;
    }
    
    return sessionId;
  }

  /**
   * Clear stored session
   */
  clearStoredSession(): void {
    localStorage.removeItem('frictionless_session_id');
    localStorage.removeItem('frictionless_session_timestamp');
    localStorage.removeItem('frictionless_has_menus');
  }
  
  /**
   * Alias for clearStoredSession
   */
  clearSessionId(): void {
    this.clearStoredSession();
  }
}

export const frictionlessTempApi = new FrictionlessTempApi();